// original adapter exports
export { router } from "@inertiajs/core";
export { default as Link } from "./Link";
export { default as createInertiaApp } from "./createInertiaApp";
export { default as useForm } from "./useForm";
export { default as usePage } from "./usePage";
export { default as useRemember } from "./useRemember";
export { default as Deferred } from "./Deferred";

declare module "@inertiajs/core" {
  export interface InertiaConfig {
    errorValueType: string[];
  }
}

// utils
export { type Page, type FormDataErrors, type FormDataConvertible } from "@inertiajs/core";
export type { InertiaForm } from "./useForm";

import {
  type ClientSideVisitOptions,
  type FormDataConvertible,
  type Page,
  type PageProps,
  type ReloadOptions,
  type VisitOptions,
  mergeDataIntoQueryString,
  router,
} from "@inertiajs/core";
import { createMemo, type Accessor } from "solid-js";

// like router.reload but for client only navigation
// can set props and other data as well
export function clientReload<TProps extends PageProps>(
  options: Omit<ClientSideVisitOptions<TProps>, "url"> & {
    data?: Record<string, FormDataConvertible>;
    action?: "replace" | "push";
    mergeStrategy?: "merge" | "replace";
  },
) {
  const currentUrl =
    (options.mergeStrategy ?? "merge") === "merge"
      ? new URL(window.location.href)
      : new URL(window.location.pathname, window.location.href);
  const [href, _data] = mergeDataIntoQueryString("get", currentUrl.href, options.data ?? {});

  router[options.action ?? "replace"]({
    ...options,
    url: href,
    preserveState: options.preserveState ?? true,
    preserveScroll: options.preserveScroll ?? true,
  });
}

const visitsInFlight = new Set<object>();
const afterVisitsQueue = new Set<() => void>();
if (typeof document !== "undefined") {
  document.addEventListener("inertia:start", (event) => {
    visitsInFlight.add(event.detail.visit);
  });
  document.addEventListener("inertia:finish", (event) => {
    visitsInFlight.delete(event.detail.visit);
    flushAfterVisits();
  });
}

function flushAfterVisits() {
  setTimeout(() => {
    if (visitsInFlight.size > 0) return;
    const ready = [...afterVisitsQueue];
    afterVisitsQueue.clear();
    for (const fn of ready) fn();
  });
}

/** Runs `fn` once, a task after the last visit in flight lands: a landing visit puts back the URL it started from
 * and loses one written while it lands, so a URL the page writes on its own (a list's first item) waits.
 * The returned function cancels it. */
export function afterVisits(fn: () => void) {
  afterVisitsQueue.add(fn);
  flushAfterVisits();
  return () => {
    afterVisitsQueue.delete(fn);
  };
}

/** Marks a reload as a background refresh of data already on screen. <Deferred>
 * leaves its children up instead of dropping to its fallback while one is in
 * flight. Foreground reloads (a filter, a date, a page change) keep the
 * fallback as their loading feedback: stale rows under a new selection read as
 * the wrong data, where stale rows under the same selection just read as
 * not-yet-fresh. */
export const BACKGROUND_RELOAD_HEADER = "x-background-reload";

/** router.reload for refreshes of what is already on screen (closing a work
 * drawer, a live-update broadcast, a poll). Tags the visit so <Deferred>
 * content stays up (see BACKGROUND_RELOAD_HEADER), and pins preserveUrl: an
 * async reload's response restores the URL it was initiated with, so without
 * it a background response landing after the user navigated (opened a
 * `?item=` drawer, switched tabs) rewrites their URL out from under them. */
export function backgroundReload(options: ReloadOptions = {}) {
  router.reload({
    ...options,
    preserveUrl: true,
    headers: { ...options.headers, [BACKGROUND_RELOAD_HEADER]: "true" },
  });
}

/** Server visit to the current URL with query params merged in (null deletes a param).
 * Use for query-param-driven UI whose props the server only builds when the param is
 * present (e.g. modals opened via `?new_thing=1`). */
export function visitWithSearchParams(
  params: Record<string, string | null>,
  options: Omit<VisitOptions, "method" | "data"> = {},
) {
  const url = new URL(window.location.href);
  for (const [key, value] of Object.entries(params)) {
    if (value === null) {
      url.searchParams.delete(key);
    } else {
      url.searchParams.set(key, value);
    }
  }
  router.visit(url.pathname + url.search, {
    preserveScroll: true,
    preserveState: true,
    ...options,
  });
}

// TODO: change to just accept the url instead of the page
export function getSearchParams(page: Page) {
  const url = new URL(page.url, window.location.href);
  return url.searchParams;
}

/** prevents signal from updating when `page` changes but the param value is the same */
export function useSearchParam(page: Page, param: string): Accessor<string | null>;
export function useSearchParam<T>(
  page: Page,
  param: string,
  parser: (value: string) => T,
): Accessor<T | null>;
export function useSearchParam(page: Page, param: string, parser?: (value: string) => unknown) {
  return createMemo(() => {
    const value = getSearchParams(page).get(param);
    if (parser && value !== null) {
      return parser(value);
    }
    return value;
  });
}

/** prevents signal from updating when `page` changes but the param value is the same */
export function useSearchParamAll(page: Page, param: string) {
  return createMemo(() => getSearchParams(page).getAll(param));
}

/** The first message a page-level error carries under a key: an action files one string where Inertia's type says a
 *  list, so either reads as one. undefined when the key has none. */
export function pageError(errors: Record<string, unknown>, key: string): string | undefined {
  const value = errors[key];
  const first = Array.isArray(value) ? value[0] : value;
  return typeof first === "string" ? first : undefined;
}

/**
 * Reads errors for a Rails-side `hash_attribute` field. Server groups them
 * under the attribute name with `Inertia.preserve_keys` so the (data-defined)
 * sub-keys arrive verbatim — no camelize transform applied.
 */
export function hashAttributeErrors(
  errors: Record<string, unknown>,
  name: string,
): Record<string, string[]> {
  return (errors[name] ?? {}) as Record<string, string[]>;
}
